// src/components/H3POIDensityLayer.js - ПОЛНОЕ ПОКРЫТИЕ ЭКРАНА ПРИ ЗУМЕ >14
import * as h3 from 'h3-js';
import maplibregl from 'maplibre-gl';

export class H3POIDensityLayer {
  constructor(map) {
    if (!map || typeof map !== 'object') {
      throw new Error('H3POIDensityLayer: некорректный объект карты');
    }
    
    this.map = map;
    this.enabled = false;
    this.resolution = 10;
    this.is3DMode = true; // 🎯 3D ПО УМОЛЧАНИЮ
    this.sourceId = 'h3-poi-density-source';
    this.layerId = 'h3-poi-density-layer';
    this.borderLayerId = 'h3-poi-density-border';
    
    this.minZoom = 14; // 🎯 ТОЛЬКО ПРИ ЗУМЕ >14
    this.maxZoom = 20;
    this.debugMode = true;
    
    this.clickHandler = null;
    this.mouseEnterHandler = null;
    this.mouseLeaveHandler = null;
    this.moveHandler = null;
    this.zoomHandler = null;
    
    this.poiCache = new Map();
    this.lastBounds = null;
    this.lastZoom = null;
    
    console.log('🌈 3D H3 Layer - SCREEN COVERAGE AT ZOOM >14');
  }

  toggle(enabled) {
    this.enabled = enabled;
    console.log(`🌈 3D H3 Layer: toggle(${enabled})`);
    
    if (enabled) {
      this.checkZoomAndShow();
    } else {
      this.hide();
    }
  }

  toggle3D(enabled) {
    this.is3DMode = enabled;
    console.log(`🌈 3D Mode: ${enabled ? 'ON' : 'OFF'}`);
    if (this.enabled) {
      this.checkZoomAndShow();
    }
  }

  setResolution(resolution) {
    console.log(`🌈 Resolution fixed at H3-10`);
    return;
  }

  // 🎯 ПРОВЕРКА ЗУМА И ПОКАЗ
  checkZoomAndShow() {
    const currentZoom = this.map.getZoom();
    
    if (currentZoom > this.minZoom && currentZoom <= this.maxZoom) {
      if (this.map.isStyleLoaded()) {
        this.create3DGradientCoverage();
      } else {
        this.map.once('styledata', () => {
          this.create3DGradientCoverage();
        });
      }
    } else {
      this.hide();
      if (currentZoom <= this.minZoom) {
        console.log(`🔍 Zoom ${currentZoom.toFixed(1)} too low for hexagons (need >${this.minZoom})`);
      }
    }
  }

  async show() {
    return this.checkZoomAndShow();
  }

  // 🌈 СОЗДАТЬ ПОЛНОЕ 3D ПОКРЫТИЕ С ГРАДИЕНТОМ
  async create3DGradientCoverage() {
    const currentZoom = this.map.getZoom();
    
    // 🎯 СТРОГАЯ ПРОВЕРКА ЗУМА
    if (currentZoom <= this.minZoom) {
      console.log(`🚫 Zoom ${currentZoom.toFixed(1)} <= ${this.minZoom}, hiding hexagons`);
      this.hide();
      return;
    }

    console.log('🌈 === CREATING SCREEN COVERAGE AT ZOOM >14 ===');
    
    if (!this.map || !this.map.isStyleLoaded()) {
      console.error('❌ Map not ready!');
      return;
    }

    this.hide();

    try {
      const bounds = this.map.getBounds();
      const center = this.map.getCenter();
      const zoom = this.map.getZoom();
      
      console.log(`🗺️ Creating screen coverage for zoom: ${zoom.toFixed(2)}`);

      // 🔍 СОЗДАЕМ ПЛОТНОЕ ПОКРЫТИЕ ЭКРАНА
      const hexagons = this.createScreenCoverage(bounds, center, zoom);
      console.log(`✅ Generated ${hexagons.length} hexagons for screen coverage`);
      
      if (hexagons.length === 0) {
        console.error('❌ No hexagons generated!');
        return;
      }

      // 🎯 ИЗВЛЕЧЕНИЕ POI
      const allPOI = await this.extractMaximumPOI();
      console.log(`🎯 Extracted ${allPOI.length} POI for analysis`);

      // 📊 ПОДСЧИТЫВАЕМ POI В ГЕКСАГОНАХ
      const hexagonsWithPOI = this.calculatePOIDensity(hexagons, allPOI);

      // 📈 АНАЛИЗИРУЕМ РАСПРЕДЕЛЕНИЕ ДЛЯ ГРАДИЕНТА
      const poiCounts = hexagonsWithPOI.map(h => h.properties.poiCount);
      const maxPOI = Math.max(...poiCounts);
      const minPOI = Math.min(...poiCounts);
      const avgPOI = (poiCounts.reduce((a, b) => a + b, 0) / poiCounts.length).toFixed(1);
      
      console.log(`📊 Screen Coverage Stats:`);
      console.log(`   Min POI: ${minPOI}, Max POI: ${maxPOI}, Avg: ${avgPOI}`);
      console.log(`   Total hexagons: ${hexagonsWithPOI.length}`);

      // Сохраняем текущие границы для отслеживания движения
      this.lastBounds = bounds;
      this.lastZoom = zoom;

      // 🔥 СОЗДАЕМ 3D ИСТОЧНИК
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: hexagonsWithPOI
        }
      });

      // 🌈 СОЗДАЕМ 3D СЛОЙ С ПОЛНЫМ ГРАДИЕНТОМ
      if (this.is3DMode) {
        this.map.addLayer({
          id: this.layerId,
          type: 'fill-extrusion',
          source: this.sourceId,
          paint: {
            // 🏗️ ВЫСОТЫ В ГРАДИЕНТЕ (от 50м до 3000м)
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['get', 'poiCount'],
              0, 50,           // 0 POI = 50м
              1, 100,          // 1 POI = 100м
              5, 250,          // 5 POI = 250м
              10, 400,         // 10 POI = 400м
              25, 700,         // 25 POI = 700м
              50, 1200,        // 50 POI = 1200м
              100, 1800,       // 100 POI = 1800м
              200, 2500,       // 200 POI = 2500м
              500, 3000        // 500+ POI = 3000м
            ],
            
            // 🌈 ПОЛНЫЙ СПЕКТРАЛЬНЫЙ ГРАДИЕНТ
            'fill-extrusion-color': [
              'interpolate',
              ['linear'],
              ['get', 'poiCount'],
              0,   '#1a1a2e',    // Темно-синий (почти черный)
              1,   '#16213e',    // Темно-синий
              2,   '#0f3460',    // Синий
              5,   '#0e4b99',    // Средний синий
              10,  '#2e86de',    // Светло-синий
              15,  '#0abde3',    // Голубой
              25,  '#00d2d3',    // Бирюзовый
              35,  '#55a3ff',    // Светло-голубой
              50,  '#5f27cd',    // Фиолетовый
              75,  '#a55eea',    // Светло-фиолетовый
              100, '#fd79a8',    // Розовый
              150, '#fdcb6e',    // Желто-оранжевый
              200, '#e17055',    // Оранжевый
              300, '#d63031',    // Красный
              500, '#74b9ff'     // Ярко-синий (максимум)
            ],
            
            'fill-extrusion-opacity': 0.85,
            'fill-extrusion-vertical-gradient': true
          }
        });
      } else {
        // 🏢 2D режим с тем же градиентом
        this.map.addLayer({
          id: this.layerId,
          type: 'fill',
          source: this.sourceId,
          paint: {
            'fill-color': [
              'interpolate',
              ['linear'],
              ['get', 'poiCount'],
              0,   '#1a1a2e',
              1,   '#16213e',
              2,   '#0f3460',
              5,   '#0e4b99',
              10,  '#2e86de',
              15,  '#0abde3',
              25,  '#00d2d3',
              35,  '#55a3ff',
              50,  '#5f27cd',
              75,  '#a55eea',
              100, '#fd79a8',
              150, '#fdcb6e',
              200, '#e17055',
              300, '#d63031',
              500, '#74b9ff'
            ],
            'fill-opacity': 0.8
          }
        });
      }

      // 🔲 ТОНКИЕ ГРАНИЦЫ ГЕКСАГОНОВ
      this.map.addLayer({
        id: this.borderLayerId,
        type: 'line',
        source: this.sourceId,
        paint: {
          'line-color': [
            'interpolate',
            ['linear'],
            ['get', 'poiCount'],
            0,   '#ffffff10',   // Почти невидимые для пустых
            10,  '#ffffff30',   // Слабые для малого количества
            50,  '#ffffff50',   // Средние
            100, '#ffffff70',   // Яркие для большого количества
            200, '#ffffff90'    // Очень яркие для максимума
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14, 0.3,
            16, 0.8,
            18, 1.2,
            20, 1.8
          ],
          'line-opacity': 0.6
        }
      });

      this.setup3DGradientHandlers();
      this.setupMoveHandler(); // 🎯 Обработчик движения карты

      console.log(`🌈 ✅ Created screen coverage with ${hexagonsWithPOI.length} hexagons at zoom ${zoom.toFixed(2)}!`);

    } catch (error) {
      console.error('❌ Error creating screen coverage:', error);
    }
  }

  // 🔍 СОЗДАТЬ ПОЛНОЕ ПОКРЫТИЕ ЭКРАНА (УЛУЧШЕННАЯ ВЕРСИЯ)
  createScreenCoverage(bounds, center, zoom) {
    console.log('🔍 Creating FULL SCREEN coverage...');
    
    const hexagons = [];
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    
    try {
      // 🌍 МЕТОД 1: H3 polygonToCells для точного покрытия экрана
      try {
        // Расширяем границы на 10% для полного покрытия краев
        const latMargin = (ne.lat - sw.lat) * 0.1;
        const lngMargin = (ne.lng - sw.lng) * 0.1;
        
        const expandedPolygon = [
          [sw.lat - latMargin, sw.lng - lngMargin],
          [sw.lat - latMargin, ne.lng + lngMargin],
          [ne.lat + latMargin, ne.lng + lngMargin],
          [ne.lat + latMargin, sw.lng - lngMargin],
          [sw.lat - latMargin, sw.lng - lngMargin]
        ];

        const h3Indices = h3.polygonToCells([expandedPolygon], 10);
        console.log(`🌍 H3 polygonToCells returned ${h3Indices.length} indices for screen coverage`);

        if (h3Indices.length > 0 && h3Indices.length < 2000) {
          h3Indices.forEach(hexId => {
            try {
              const boundary = h3.cellToBoundary(hexId, true);
              const hexCenter = h3.cellToLatLng(hexId);
              
              hexagons.push({
                type: 'Feature',
                properties: {
                  h3Index: hexId,
                  resolution: 10,
                  poiCount: 0,
                  center: { lat: hexCenter[0], lng: hexCenter[1] }
                },
                geometry: {
                  type: 'Polygon',
                  coordinates: [boundary]
                }
              });
            } catch (error) {
              // Skip invalid hexagons
            }
          });
          
          if (hexagons.length > 0) {
            console.log(`✅ Screen coverage: Created ${hexagons.length} hexagons`);
            return hexagons;
          }
        }
      } catch (h3Error) {
        console.warn('⚠️ polygonToCells failed for screen coverage');
      }

      // 🌍 МЕТОД 2: Плотная сетка для полного покрытия экрана
      const gridDensity = Math.max(15, Math.floor(zoom * 1.5)); // Увеличиваем плотность с зумом
      const latStep = (ne.lat - sw.lat) / gridDensity;
      const lngStep = (ne.lng - sw.lng) / gridDensity;
      
      const usedIndices = new Set();
      
      // Расширяем сетку за границы экрана для полного покрытия
      const extraCells = 3;
      for (let i = -extraCells; i < gridDensity + extraCells; i++) {
        for (let j = -extraCells; j < gridDensity + extraCells; j++) {
          const lat = sw.lat + (i + 0.5) * latStep;
          const lng = sw.lng + (j + 0.5) * lngStep;
          
          try {
            const h3Index = h3.latLngToCell(lat, lng, 10);
            
            if (!usedIndices.has(h3Index)) {
              usedIndices.add(h3Index);
              
              const boundary = h3.cellToBoundary(h3Index, true);
              const hexCenter = h3.cellToLatLng(h3Index);
              
              hexagons.push({
                type: 'Feature',
                properties: {
                  h3Index: h3Index,
                  resolution: 10,
                  poiCount: 0,
                  center: { lat: hexCenter[0], lng: hexCenter[1] },
                  gridPos: `${i}-${j}`
                },
                geometry: {
                  type: 'Polygon',
                  coordinates: [boundary]
                }
              });
            }
          } catch (h3Error) {
            // Skip invalid points
          }
        }
      }

      console.log(`✅ Grid coverage: Created ${hexagons.length} unique hexagons`);

    } catch (error) {
      console.error('❌ Error creating screen coverage:', error);
    }
    
    console.log(`🌈 ✅ Total screen coverage: ${hexagons.length} hexagons`);
    return hexagons;
  }

  // 🔄 ОБРАБОТЧИК ДВИЖЕНИЯ КАРТЫ
  setupMoveHandler() {
    let updateTimeout;
    
    this.moveHandler = () => {
      // Debounce обновления
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        if (this.enabled) {
          const currentZoom = this.map.getZoom();
          const currentBounds = this.map.getBounds();
          
          // 🎯 ПРОВЕРЯЕМ ЗУМЫ
          if (currentZoom <= this.minZoom) {
            this.hide();
            return;
          }
          
          // Обновление при изменении зума или существенном изменении границ
          const zoomChanged = Math.abs(currentZoom - (this.lastZoom || 0)) > 0.5;
          
          let boundsChanged = false;
          if (this.lastBounds) {
            const oldSW = this.lastBounds.getSouthWest();
            const oldNE = this.lastBounds.getNorthEast();
            const newSW = currentBounds.getSouthWest();
            const newNE = currentBounds.getNorthEast();
            
            const threshold = 0.002; // Уменьшаем порог для более частых обновлений
            boundsChanged = 
              Math.abs(oldSW.lat - newSW.lat) > threshold ||
              Math.abs(oldSW.lng - newSW.lng) > threshold ||
              Math.abs(oldNE.lat - newNE.lat) > threshold ||
              Math.abs(oldNE.lng - newNE.lng) > threshold;
          } else {
            boundsChanged = true;
          }
          
          if (zoomChanged || boundsChanged) {
            console.log(`🔄 Updating screen coverage for new view (zoom: ${currentZoom.toFixed(1)})`);
            this.create3DGradientCoverage();
          }
        }
      }, 300); // Быстрее обновляем для лучшей отзывчивости
    };

    this.map.on('moveend', this.moveHandler);
    
    // 🎯 Дополнительно слушаем изменения зума для быстрого скрытия/показа
    this.zoomHandler = () => {
      if (this.enabled) {
        this.checkZoomAndShow();
      }
    };
    
    this.map.on('zoom', this.zoomHandler);
  }

  // 🎯 ИЗВЛЕЧЕНИЕ POI (упрощенная версия для производительности)
  async extractMaximumPOI() {
    console.log('🎯 Extracting POI for screen coverage...');
    const allPOI = [];
    
    try {
      const bounds = this.map.getBounds();
      
      // Source features
      try {
        const sourceFeatures = this.map.querySourceFeatures('mapi-tiles', {
          sourceLayer: 'pois'
        });
        
        sourceFeatures.forEach(feature => {
          const coords = this.extractCoordinatesAggressive(feature);
          if (coords && this.isInBounds(coords, bounds)) {
            allPOI.push({
              lat: coords.lat,
              lng: coords.lng,
              type: feature.properties?.kind || 'unknown',
              name: feature.properties?.name || '',
              source: 'mapi'
            });
          }
        });
        
        console.log(`✅ Extracted ${allPOI.length} POI from MAPI for screen coverage`);
      } catch (error) {
        console.warn('⚠️ POI extraction error:', error);
      }

    } catch (error) {
      console.error('❌ POI extraction error:', error);
    }
    
    return allPOI;
  }

  extractCoordinatesAggressive(feature) {
    if (!feature.geometry?.coordinates) return null;
    
    const coords = feature.geometry.coordinates;
    if (feature.geometry.type === 'Point') {
      return { lat: coords[1], lng: coords[0] };
    }
    return null;
  }

  isInBounds(coords, bounds) {
    return coords.lat >= bounds.getSouth() && 
           coords.lat <= bounds.getNorth() &&
           coords.lng >= bounds.getWest() && 
           coords.lng <= bounds.getEast();
  }

  calculatePOIDensity(hexagons, allPOI) {
    console.log(`📊 Calculating POI density for screen coverage...`);
    
    hexagons.forEach(hexagon => {
      const h3Index = hexagon.properties.h3Index;
      let count = 0;
      
      allPOI.forEach(poi => {
        try {
          const poiH3Index = h3.latLngToCell(poi.lat, poi.lng, 10);
          if (poiH3Index === h3Index) {
            count++;
          }
        } catch (error) {
          // Ignore
        }
      });
      
      hexagon.properties.poiCount = count;
      hexagon.properties.category = this.categorizeForGradient(count);
    });
    
    return hexagons;
  }

  categorizeForGradient(poiCount) {
    if (poiCount === 0) return 'empty';
    if (poiCount < 5) return 'very-low';
    if (poiCount < 15) return 'low';
    if (poiCount < 35) return 'medium';
    if (poiCount < 75) return 'high';
    if (poiCount < 150) return 'very-high';
    return 'extreme';
  }

  // 🔥 3D GRADIENT HANDLERS
  setup3DGradientHandlers() {
    this.clickHandler = (e) => {
      if (e.features.length > 0) {
        const feature = e.features[0];
        const props = feature.properties;
        
        // Высота для отображения
        const height = this.calculateDisplayHeight(props.poiCount);
        
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`
            <div style="padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border-radius: 12px; font-family: system-ui; min-width: 280px;">
              <h3 style="margin: 0 0 15px 0; text-align: center; font-size: 18px;">🌈 3D Screen Coverage</h3>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 15px;">
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.2); border-radius: 8px; backdrop-filter: blur(10px);">
                  <div style="font-size: 24px; font-weight: bold;">${props.poiCount}</div>
                  <div style="font-size: 10px; opacity: 0.9;">POI COUNT</div>
                </div>
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.2); border-radius: 8px; backdrop-filter: blur(10px);">
                  <div style="font-size: 24px; font-weight: bold;">${height}m</div>
                  <div style="font-size: 10px; opacity: 0.9;">HEIGHT</div>
                </div>
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.2); border-radius: 8px; backdrop-filter: blur(10px);">
                  <div style="font-size: 24px; font-weight: bold;">H3-10</div>
                  <div style="font-size: 10px; opacity: 0.9;">RESOLUTION</div>
                </div>
              </div>
              
              <div style="margin-bottom: 10px;">
                <strong>Category:</strong> <span style="text-transform: uppercase; letter-spacing: 1px;">${props.category}</span>
              </div>
              <div style="margin-bottom: 10px;">
                <strong>Zoom:</strong> ${this.map.getZoom().toFixed(2)} (>14 required)
              </div>
              <div style="margin-bottom: 10px;">
                <strong>3D Mode:</strong> ${this.is3DMode ? '✅ ENABLED' : '❌ DISABLED'}
              </div>
              <div style="font-size: 12px; opacity: 0.8; margin-top: 10px;">
                <strong>H3 Index:</strong><br><code style="background: rgba(0,0,0,0.3); padding: 2px 4px; border-radius: 3px; font-size: 10px;">${props.h3Index}</code>
              </div>
            </div>
          `)
          .addTo(this.map);
      }
    };

    this.map.on('click', this.layerId, this.clickHandler);
    
    this.mouseEnterHandler = () => {
      this.map.getCanvas().style.cursor = 'pointer';
    };

    this.mouseLeaveHandler = () => {
      this.map.getCanvas().style.cursor = '';
    };

    this.map.on('mouseenter', this.layerId, this.mouseEnterHandler);
    this.map.on('mouseleave', this.layerId, this.mouseLeaveHandler);
  }

  calculateDisplayHeight(poiCount) {
    if (poiCount === 0) return 50;
    if (poiCount < 5) return 100 + poiCount * 30;
    if (poiCount < 25) return 250 + (poiCount - 5) * 22;
    if (poiCount < 100) return 700 + (poiCount - 25) * 15;
    if (poiCount < 200) return 1200 + (poiCount - 100) * 8;
    return Math.min(3000, 2000 + (poiCount - 200) * 5);
  }

  // Остальные методы (hide, removeEventHandlers, destroy и т.д.)
  hide() {
    if (!this.map?.getStyle) return;

    try {
      if (this.map.getLayer?.(this.layerId)) {
        this.map.removeLayer(this.layerId);
      }
      
      if (this.map.getLayer?.(this.borderLayerId)) {
        this.map.removeLayer(this.borderLayerId);
      }

      if (this.map.getSource?.(this.sourceId)) {
        this.map.removeSource(this.sourceId);
      }

      this.removeEventHandlers();
    } catch (error) {
      console.warn('Hide error:', error);
      this.removeEventHandlers();
    }
  }

  async updateLayer() {
    if (this.enabled) {
      this.checkZoomAndShow();
    }
  }

  removeEventHandlers() {
    if (!this.map?.off) return;

    try {
      if (this.clickHandler) {
        this.map.off('click', this.layerId, this.clickHandler);
        this.clickHandler = null;
      }
      
      if (this.mouseEnterHandler) {
        this.map.off('mouseenter', this.layerId, this.mouseEnterHandler);
        this.mouseEnterHandler = null;
      }
      
      if (this.mouseLeaveHandler) {
        this.map.off('mouseleave', this.layerId, this.mouseLeaveHandler);
        this.mouseLeaveHandler = null;
      }

      if (this.moveHandler) {
        this.map.off('moveend', this.moveHandler);
        this.moveHandler = null;
      }
      
      if (this.zoomHandler) {
        this.map.off('zoom', this.zoomHandler);
        this.zoomHandler = null;
      }
    } catch (error) {
      console.warn('Handler removal error:', error);
    }
  }

  getStats() {
    return {
      enabled: this.enabled,
      resolution: 10,
      is3DMode: this.is3DMode,
      debugMode: this.debugMode,
      currentZoom: this.map?.getZoom?.() || 0,
      minZoom: this.minZoom
    };
  }

  destroy() {
    this.hide();
    this.poiCache.clear();
    this.lastBounds = null;
    this.lastZoom = null;
    this.map = null;
  }
}